import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';
import { Role, ProjectStatus, View } from '../constants/roles';
import { Button } from 'antd';
import { Modal } from '../components/Modal';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageTabs from '../components/page/PageTabs';
import DataCard from '../components/page/DataCard';
import EnrichmentKeyNotice from '../components/EnrichmentKeyNotice';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import { describeRejectedFields } from "../utils/scopingAiResults";

function formatWorkspaceAllotted(iso) {
    if (!iso) return '-';
    return formatDdMmYyyyTime(iso);
}

/**
 * Dashboard / Portfolio Plane view.
 * Replaced with exact Tailwind UI from plen-main reference components.
 */
const DashboardView = () => {
    const { user, projects, projectsLoading, selectProject, addProject, toggleProjectStatus, deleteProject, fetchProjects, currentView } = useApp();
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const currentViewRef = useRef(currentView);
    useEffect(() => {
        currentViewRef.current = currentView;
    }, [currentView]);

    // Equivalent roles mapping to plen-main types
    const isAdmin = user?.role === Role.ADMIN;
    // Note: Lead Advisor is conceptually LEAD_ADVISOR
    const isLeadAdvisor = user?.role === Role.LEAD_ADVISOR;
    const isCompanySpoc = user?.role === Role.COMPANY_SPOC;

    const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);

    const [newProjectMeta, setNewProjectMeta] = useState({ projectName: '', certification: '' });
    const [companyFormData, setCompanyFormData] = useState({
        legalName: '',
        website: '',
        linkedin: '',
        contactName: '',
        contactNumber: '',
        contactEmail: '',
        billingAddress: '',
        billToPersonName: '',
        billToDepartment: '',
        billingCurrency: 'INR'
    });

    const [aiProfilingId, setAiProfilingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [portfolioTab, setPortfolioTab] = useState('projects');

    // Scoping-stage framework picker (CLAUDE.md §4 stage 2). Sourced live from the
    // backend standards registry (`GET /api/v1/standards?selectableOnly=1`) rather than
    // a hardcoded option list — see .claude/lessons/standard-registry-status-doesnt-gate-the-real-picker.md.
    // 'error' is a fail-closed state: never fall back to a hardcoded list on failure.
    const [frameworkStatus, setFrameworkStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [frameworkOptions, setFrameworkOptions] = useState([]);
    const [frameworkRetryToken, setFrameworkRetryToken] = useState(0);

    useEffect(() => {
        if (!isAddCompanyOpen) return;
        let cancelled = false;
        setFrameworkStatus('loading');
        setFrameworkOptions([]);
        (async () => {
            try {
                // Read the active account fresh inside the effect rather than depending on
                // the outer `activeAccount` closure variable: `instance.getActiveAccount()`
                // returns a new object reference on every render, so putting it in this
                // effect's dependency array (below) previously caused an infinite
                // render/effect loop (setFrameworkOptions([]) also produces a new array
                // reference each run, so React never bailed out of the re-render).
                const account = instance.getActiveAccount();
                const tok = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
                const res = await fetch(`${apiConfig.baseUrl}/api/v1/standards?selectableOnly=1`, {
                    headers: { Authorization: `Bearer ${tok.accessToken}` },
                });
                const json = await res.json().catch(() => null);
                const standards = json?.data?.standards;
                if (!res.ok || !json?.success || !Array.isArray(standards)) {
                    throw new Error(json?.error?.message || 'Failed to load frameworks');
                }
                if (cancelled) return;
                setFrameworkOptions(standards);
                setFrameworkStatus('ready');
            } catch (err) {
                if (cancelled) return;
                console.error('Failed to load selectable standards:', err);
                setFrameworkOptions([]);
                setFrameworkStatus('error');
            }
        })();
        return () => { cancelled = true; };
        // Deliberately NOT depending on `activeAccount` (recomputed every render from
        // `instance.getActiveAccount()`, a fresh reference each call) — see comment above.
        // `instance` (the MSAL PublicClientApplication) is stable for the app's lifetime.
        // NOTE: react-hooks/exhaustive-deps is not currently loaded by this build's ESLint
        // config (see .claude/lessons/eslint-plugin-react-hooks-breaks-cra-build.md) — an
        // actual `eslint-disable-next-line` directive here fails the build because ESLint
        // can't find the rule to disable. This comment preserves the rationale above; if
        // the full `react-app` ESLint config is ever wired in, re-add a real disable
        // directive at that point.
    }, [isAddCompanyOpen, instance, frameworkRetryToken]);

    const handleDeleteProject = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const result = await deleteProject(deleteTarget.id);
            if (!result?.ok) {
                alert(
                    result?.errorMessage ||
                        'Failed to delete project. You may not have permission.',
                );
            }
        } catch (err) {
            console.error('Delete failed:', err);
            alert('An error occurred while deleting the project.');
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleCommitCompany = async () => {
        if (!companyFormData.legalName || !newProjectMeta.projectName) return;
        // Fail closed: never let project creation proceed with an unloaded/failed
        // framework list — see .claude/lessons/standard-registry-status-doesnt-gate-the-real-picker.md.
        if (frameworkStatus !== 'ready' || !newProjectMeta.certification) {
            alert('Select a target framework before initializing the governance plane.');
            return;
        }

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...apiTokenRequest,
                account: activeAccount,
            });

            const response = await fetch(`${apiConfig.baseUrl}/api/v1/companies`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({
                    legalName: companyFormData.legalName,
                    website: companyFormData.website,
                    linkedIn: companyFormData.linkedin,
                    contactName: companyFormData.contactName,
                    contactEmail: companyFormData.contactEmail,
                    contactPhone: companyFormData.contactNumber,
                    billingAddress: companyFormData.billingAddress,
                    billToPersonName: companyFormData.billToPersonName,
                    billToDepartment: companyFormData.billToDepartment,
                    billingCurrency: companyFormData.billingCurrency,
                    projectName: newProjectMeta.projectName,
                    frameworkType: newProjectMeta.certification || 'Custom',
                }),
            });

            const json = await response.json();

            if (!response.ok || !json.success) {
                throw new Error(json.error?.message || 'Failed to create company');
            }

            const companyId = json.data.company?.id;
            const newProjectId = json.data.project?.id;
            console.log('Company created:', json.data);

            await fetchProjects();

            setIsAddCompanyOpen(false);
            setNewProjectMeta({ projectName: '', certification: '' });
            setCompanyFormData({
                legalName: '', website: '', linkedin: '', contactName: '', contactNumber: '', contactEmail: '',
                billingAddress: '', billToPersonName: '', billToDepartment: '', billingCurrency: 'INR'
            });

            if (companyId && newProjectId) {
                setAiProfilingId(companyId);
                try {
                    const tok = await instance.acquireTokenSilent({ ...apiTokenRequest, account: activeAccount });
                    const aiRes = await fetch(`${apiConfig.baseUrl}/api/v1/companies/${companyId}/ai-profile`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok.accessToken}` },
                    });
                    if (!aiRes.ok) {
                        const errJson = await aiRes.json().catch(() => ({}));
                        throw new Error(errJson.error?.message || 'AI profiling failed');
                    }
                    // Fields the AI could not source are left blank on the company: tell the Lead Advisor
                    // which ones to fill in (user ruling: absent is acceptable, fake is not).
                    const aiJson = await aiRes.json().catch(() => null);
                    const rejectedNote = describeRejectedFields(aiJson?.data?.rejectedFields);
                    if (rejectedNote) alert(rejectedNote);
                    const list = await fetchProjects();
                    if (currentViewRef.current === View.DASHBOARD && list) {
                        const p = list.find((x) => x.id === newProjectId);
                        if (p) {
                            selectProject(p, { initialWorkspaceStep: 1, initialProfilingSubTab: 'onboarding' });
                        }
                    }
                } catch (err) {
                    console.warn('AI profiling or navigation failed:', err);
                    alert(err?.message || 'AI profiling failed. Open the project from the list when ready.');
                } finally {
                    setAiProfilingId(null);
                }
            }

        } catch (error) {
            console.error('Company creation failed:', error);
            alert(`Failed to create company: ${error.message}`);
        }
    };

    const visibleProjects = projects.filter(p => {
        if (isAdmin || user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADVISOR || user?.role === Role.COMPANY_SPOC) return true;
        if (p.leadAdvisor === user?.name) return true;
        return p.members?.some(m => m.id === user?.id);
    });

    const riskReviewProjects = visibleProjects.filter(
        (p) => p.riskRegisterWorkflow === 'in_review',
    );

    const showPortfolioRiskTab = isAdmin || isLeadAdvisor;

    const tableColSpan = 4;

    return (
        <PageShell>
            <PageHeader
                title={`${isAdmin ? 'System' : isCompanySpoc ? 'Company' : isLeadAdvisor ? 'Portfolio' : 'Control'} Plane`}
                subtitle={<>Managing GRC Governance for <strong>{user?.name}</strong></>}
                actions={isLeadAdvisor ? (
                    <Button type="primary" onClick={() => setIsAddCompanyOpen(true)}>
                        + Add Company
                    </Button>
                ) : null}
            />

            {showPortfolioRiskTab ? (
                <PageTabs
                    activeKey={portfolioTab}
                    onChange={setPortfolioTab}
                    items={[
                        { key: 'projects', label: 'Projects' },
                        { key: 'risk_review', label: 'Risk register review', count: riskReviewProjects.length > 0 ? riskReviewProjects.length : undefined },
                    ]}
                />
            ) : null}

            {portfolioTab === 'projects' || !showPortfolioRiskTab ? (
                <DataCard className="overflow-hidden" styles={{ body: { padding: 0 } }}>
                <div data-testid="projects-section-header" className="px-6 lg:px-10 py-8 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-4 bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]">
                    <h2 className="text-2xl font-black text-[color:var(--color-text-hero)] tracking-tight font-outfit">Projects</h2>
                    <div className="shrink-0 px-4 py-2 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-2xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-info-line)]">
                        {projectsLoading ? (
                            <span className="inline-flex items-center gap-2 normal-case font-bold tracking-normal text-[11px]">
                                <svg className="animate-spin h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Syncing registry…
                            </span>
                        ) : (
                            `${visibleProjects.length} Authorized Projects`
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto projects-plane-table">
                    <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
                        <colgroup>
                            <col style={{ width: '36%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '28%' }} />
                        </colgroup>
                        <thead>
                            <tr className="text-[10px] font-extrabold text-[color:var(--color-text-muted)] uppercase tracking-widest border-b border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_10%,transparent)]">
                                <th className="px-6 lg:px-10 py-6 align-bottom">Engagement Name & Entity</th>
                                <th className="px-6 lg:px-10 py-6 align-bottom">Target Compliance</th>
                                <th className="px-6 lg:px-10 py-6 align-bottom">Maturity Lifecycle</th>
                                <th className="px-6 lg:px-10 py-6 text-right align-bottom">Action</th>
                            </tr>
                        </thead>
                            {projectsLoading ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={tableColSpan} className="px-10 py-24">
                                            <div className="flex flex-col items-center justify-center gap-5 text-center">
                                                <div className="relative">
                                                    <div className="absolute inset-0 rounded-full bg-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)] blur-xl scale-150 animate-pulse" aria-hidden />
                                                    <svg className="relative h-12 w-12 text-[color:var(--color-accent)] animate-spin" viewBox="0 0 24 24" aria-hidden>
                                                        <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                                                        <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                </div>
                                                <div className="space-y-1">
                                                    <p className="text-sm font-black text-[color:var(--color-text-hero)] tracking-tight">Loading your engagements</p>
                                                    <p className="text-xs text-[color:var(--color-text-muted)] font-medium max-w-sm mx-auto">Fetching projects from the governance plane. This usually takes a moment.</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : visibleProjects.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={tableColSpan} className="px-10 py-20 text-center text-[color:var(--color-text-muted)] font-black uppercase tracking-widest text-[10px]">
                                            No project memberships found.
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                visibleProjects.map((project) => {
                                    const wt = project.workspaceTask;
                                    const rowMuted = project.isDisabled ? 'opacity-50 grayscale' : '';
                                    const actionButtons = (
                                        <div
                                            className="flex w-full flex-row flex-nowrap items-center justify-end gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {(isAdmin || isLeadAdvisor) && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteTarget(project);
                                                    }}
                                                    title="Delete project"
                                                    className="p-2.5 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] rounded-xl hover:bg-[color:var(--color-danger-bg)] hover:text-[color:var(--color-danger-fg)] transition-all hover:scale-110 active:scale-90 shrink-0"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            )}
                                            {isAdmin && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleProjectStatus(project.id);
                                                    }}
                                                    className={`px-3 py-1.5 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all shrink-0 ${project.isDisabled
                                                        ? 'bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] hover:bg-[color:var(--color-ok-bg)]'
                                                        : 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border border-[color:var(--color-danger-line)] hover:bg-[color:var(--color-danger-bg)]'
                                                        }`}
                                                >
                                                    {project.isDisabled ? 'Enable' : 'Disable'}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectProject(project);
                                                }}
                                                className="p-3 bg-[color:var(--color-accent-fill)] text-white rounded-2xl hover:brightness-110 transition-all shadow-xl hover:scale-110 active:scale-90 shrink-0"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                            </button>
                                        </div>
                                    );
                                    return (
                                        <tbody
                                            key={project.id}
                                            className={`border-b border-[color:var(--color-border)] last:border-b-0 group transition-colors ${rowMuted}`}
                                        >
                                            <tr
                                                className="cursor-pointer hover:bg-[color:color-mix(in_srgb,var(--color-accent-tint)_30%,transparent)]"
                                                onClick={() => selectProject(project)}
                                            >
                                                <td className="px-6 lg:px-10 py-8 align-top min-w-0">
                                                    <div className="flex items-start gap-3 min-w-0">
                                                        <div className="min-w-0">
                                                            <div className={`text-base font-black text-[color:var(--color-text-hero)] group-hover:text-[color:var(--color-accent)] transition-colors ${project.isDisabled ? 'line-through' : ''}`}>
                                                                {project.name}
                                                            </div>
                                                            <div className="text-[10px] text-[color:var(--color-text-muted)] font-bold uppercase mt-1.5 tracking-wide flex flex-wrap items-center gap-2">
                                                                {project.companyName} {project.memberCount ? `• ${project.memberCount} members` : ''} {project.leadAdvisor ? `• Lead: ${project.leadAdvisor}` : ''}
                                                                {aiProfilingId && aiProfilingId === project.companyId && (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border border-[color:var(--color-info-line)] rounded-lg animate-pulse">
                                                                        <svg className="animate-spin h-2.5 w-2.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                                                        AI Profiling...
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {project.isDisabled && (
                                                            <span className="px-2 py-0.5 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] rounded text-[8px] font-black uppercase tracking-widest shrink-0">Disabled</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 lg:px-10 py-8 align-top min-w-0">
                                                    <span className="inline-flex max-w-full px-4 py-2 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-xl text-[11px] font-black tracking-wide border border-[color:var(--color-info-line)] uppercase break-words text-left">
                                                        {project.certification || project.standards?.[0] || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-6 lg:px-10 py-8 align-top min-w-0">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 min-w-0 max-w-[160px] h-2.5 bg-[color:var(--color-bg-alt)] rounded-full overflow-hidden border border-[color:color-mix(in_srgb,var(--color-border)_50%,transparent)]">
                                                            <div className={`h-full bg-gradient-to-r from-[color:var(--color-accent-fill)] to-[color:var(--color-accent-fill)] transition-all duration-1000`} style={{ width: `${project.progress ?? 0}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-black text-[color:var(--color-text-hero)] tabular-nums shrink-0">{project.progress ?? 0}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 lg:px-10 py-8 align-top text-right min-w-0">
                                                    {actionButtons}
                                                </td>
                                            </tr>
                                            {isAdmin && (
                                                <tr
                                                    className="cursor-pointer hover:bg-[color:color-mix(in_srgb,var(--color-bg-alt)_90%,transparent)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_70%,transparent)]"
                                                    onClick={() => selectProject(project)}
                                                >
                                                    <td colSpan={4} className="px-6 lg:px-10 py-4 align-middle border-t border-[color:color-mix(in_srgb,var(--color-border)_90%,transparent)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_50%,transparent)]">
                                                        <div className="flex flex-nowrap items-center gap-x-4 overflow-x-auto pb-0.5 text-sm [-webkit-overflow-scrolling:touch]">
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] shrink-0">
                                                                Workspace task
                                                            </span>
                                                            <span className="hidden sm:inline h-3.5 w-px shrink-0 bg-[color:var(--color-bg-alt)]" aria-hidden />
                                                            <span className="inline-flex shrink-0 items-baseline gap-1.5 whitespace-nowrap">
                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Section</span>
                                                                <span className="font-bold text-[color:var(--color-text-hero)]">{wt?.section ?? '-'}</span>
                                                            </span>
                                                            <span className="text-[color:var(--color-border-strong)] select-none" aria-hidden>|</span>
                                                            <span className="inline-flex shrink-0 items-baseline gap-1.5 whitespace-nowrap">
                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Sub-tab</span>
                                                                <span className="text-[color:var(--color-text-hero)]">{wt?.subTab ?? '-'}</span>
                                                            </span>
                                                            <span className="text-[color:var(--color-border-strong)] select-none" aria-hidden>|</span>
                                                            <span className="inline-flex shrink-0 items-baseline gap-1.5">
                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Allotted</span>
                                                                <span className="tabular-nums text-[color:var(--color-text-hero)]">{wt ? formatWorkspaceAllotted(wt.allottedAt) : '-'}</span>
                                                            </span>
                                                            <span className="text-[color:var(--color-border-strong)] select-none" aria-hidden>|</span>
                                                            <span className="inline-flex shrink-0 items-baseline gap-1.5 whitespace-nowrap">
                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Assignee</span>
                                                                <span className="text-[color:var(--color-text-hero)]">{wt?.assigneeRole ?? '-'}</span>
                                                            </span>
                                                            <span className="text-[color:var(--color-border-strong)] select-none" aria-hidden>|</span>
                                                            <span className="inline-flex shrink-0 items-baseline gap-1.5">
                                                                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Status</span>
                                                                {wt ? (
                                                                    <span
                                                                        className={`inline-flex px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                                                                            wt.status === 'complete'
                                                                                ? 'border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]'
                                                                                : 'border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]'
                                                                        }`}
                                                                    >
                                                                        {wt.status === 'complete' ? 'Complete' : 'In progress'}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[color:var(--color-text-muted)]">-</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    );
                                })
                            )}
                    </table>
                </div>
                </DataCard>
            ) : (
                <DataCard className="overflow-hidden" styles={{ body: { padding: 0 } }}>
                    <div data-testid="projects-section-header" className="px-6 lg:px-10 py-8 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-4 bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]">
                        <h2 className="text-2xl font-black text-[color:var(--color-text-hero)] tracking-tight font-outfit">Risk register review</h2>
                        <p className="text-xs font-bold text-[color:var(--color-text-muted)]">Awaiting your approval (in_review)</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] border-collapse text-left">
                            <thead>
                                <tr className="text-[10px] font-extrabold text-[color:var(--color-text-muted)] uppercase tracking-widest border-b border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_10%,transparent)]">
                                    <th className="px-6 lg:px-10 py-5">Engagement</th>
                                    <th className="px-6 lg:px-10 py-5">Company</th>
                                    <th className="px-6 lg:px-10 py-5">Submitted</th>
                                    <th className="px-6 lg:px-10 py-5 text-right">Open</th>
                                </tr>
                            </thead>
                            <tbody>
                                {riskReviewProjects.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-10 py-16 text-center text-sm font-medium text-[color:var(--color-text-muted)]">
                                            No engagements awaiting risk register review.
                                        </td>
                                    </tr>
                                ) : (
                                    riskReviewProjects.map((p) => (
                                        <tr key={p.id} className="border-b border-[color:var(--color-border)]">
                                            <td className="px-6 lg:px-10 py-5 font-bold text-[color:var(--color-text-hero)]">{p.name}</td>
                                            <td className="px-6 lg:px-10 py-5 text-sm text-[color:var(--color-text-muted)]">{p.companyName || '-'}</td>
                                            <td className="px-6 lg:px-10 py-5 text-sm tabular-nums text-[color:var(--color-text-muted)]">
                                                {p.riskRegisterSubmittedAt
                                                    ? formatWorkspaceAllotted(p.riskRegisterSubmittedAt)
                                                    : '-'}
                                            </td>
                                            <td className="px-6 lg:px-10 py-5 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        selectProject(p, {
                                                            initialWorkspaceStep: 6,
                                                            initialRiskAnalysisSubTab: 'register',
                                                            forceWorkbench: true,
                                                        })
                                                    }
                                                    className="rounded-xl bg-[color:var(--color-accent-fill)] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white hover:brightness-110"
                                                >
                                                    Open workbench
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </DataCard>
            )}

            {/* Add Company Modal */}
            <Modal
                isOpen={isAddCompanyOpen}
                onClose={() => setIsAddCompanyOpen(false)}
                title="Add New Company"
                subtitle="Create the legal entity and first engagement in one step. You can refine profiling and members after setup."
                size="xl"
                footer={
                    <button
                        type="button"
                        onClick={handleCommitCompany}
                        disabled={frameworkStatus !== 'ready'}
                        className="w-full rounded-2xl bg-[color:var(--color-accent-fill)] py-4 text-xs font-black uppercase tracking-[0.2em] text-white shadow-lg shadow-blue-600/25 transition-all hover:brightness-110 active:scale-[0.99] sm:py-5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-110"
                    >
                        Initialize Governance Plane
                    </button>
                }
            >
                <div className="space-y-8">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[color:var(--color-accent-fill)] via-[color:var(--color-accent-fill)] to-[color:var(--color-accent-fill)] p-6 shadow-lg ring-1 ring-white/10 sm:p-7">
                        <div className="pointer-events-none absolute -right-8 -top-8 h-36 w-36 rounded-full bg-[color:color-mix(in_srgb,var(--color-accent-fill)_15%,transparent)] blur-3xl" aria-hidden />
                        <div className="relative space-y-5">
                            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400">Engagement setup</div>
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-white/70">Project name</label>
                                    <input
                                        type="text"
                                        value={newProjectMeta.projectName}
                                        onChange={e => setNewProjectMeta({ ...newProjectMeta, projectName: e.target.value })}
                                        placeholder="e.g. FY25 Enterprise Audit"
                                        className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold text-white outline-none placeholder:text-white/50 focus:border-[color:color-mix(in_srgb,var(--color-accent)_50%,transparent)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_40%,transparent)]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-white/70">Target framework</label>
                                    <select
                                        value={newProjectMeta.certification}
                                        onChange={e => setNewProjectMeta({ ...newProjectMeta, certification: e.target.value })}
                                        disabled={frameworkStatus !== 'ready'}
                                        className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold text-white outline-none focus:border-[color:color-mix(in_srgb,var(--color-accent)_50%,transparent)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_40%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
                                        style={{ color: 'white' }}
                                    >
                                        {/* The <select> itself is intentionally dark-themed (bg-white/10 +
                                            text-white above) to match this panel's dark "Engagement Setup"
                                            background. Browsers do not apply that same background-color to
                                            the native <option> popup list, but `color` on the <select> IS
                                            inherited onto its <option>s -- so without an explicit override
                                            here, the popup rendered white text on the browser's own default
                                            (white) list background: invisible except where an option was
                                            highlighted. `color`/`backgroundColor` are two of the few style
                                            properties browsers reliably honour directly on <option>, so set
                                            them explicitly here rather than relying on inheritance. */}
                                        {frameworkStatus === 'loading' && (
                                            <option value="" style={{ color: 'var(--color-text-hero)', backgroundColor: 'var(--color-bg-white)' }}>Loading frameworks…</option>
                                        )}
                                        {frameworkStatus === 'error' && (
                                            <option value="" style={{ color: 'var(--color-text-hero)', backgroundColor: 'var(--color-bg-white)' }}>Unable to load frameworks - please retry</option>
                                        )}
                                        {frameworkStatus === 'ready' && (
                                            <>
                                                <option value="" style={{ color: 'var(--color-text-hero)', backgroundColor: 'var(--color-bg-white)' }}>Select framework</option>
                                                {frameworkOptions.map(s => (
                                                    <option key={s.id || s.displayName} value={s.displayName} style={{ color: 'var(--color-text-hero)', backgroundColor: 'var(--color-bg-white)' }}>{s.displayName}</option>
                                                ))}
                                            </>
                                        )}
                                    </select>
                                    {frameworkStatus === 'error' && (
                                        <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:color-mix(in_srgb,var(--color-danger-fg)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--color-danger-fg)_10%,transparent)] px-3 py-2">
                                            <span className="text-[11px] font-bold text-red-300">Unable to load frameworks - please retry.</span>
                                            <button
                                                type="button"
                                                onClick={() => setFrameworkRetryToken(t => t + 1)}
                                                className="shrink-0 rounded-lg bg-[color:color-mix(in_srgb,var(--color-danger-fg)_20%,transparent)] px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-200 hover:bg-[color:color-mix(in_srgb,var(--color-danger-fg)_30%,transparent)]"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="h-px flex-1 bg-[color:var(--color-bg-alt)]"></div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Entity Details</span>
                            <div className="h-px flex-1 bg-[color:var(--color-bg-alt)]"></div>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Legal Company Name</label>
                                <input
                                    type="text"
                                    value={companyFormData.legalName}
                                    onChange={e => setCompanyFormData({ ...companyFormData, legalName: e.target.value })}
                                    placeholder="e.g. Zerodha Broking Limited"
                                    className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-base font-bold text-[color:var(--color-text-hero)] outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_15%,transparent)]"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Website</label>
                                    <input type="text" value={companyFormData.website} onChange={e => setCompanyFormData({ ...companyFormData, website: e.target.value })} className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-sm font-bold outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">LinkedIn</label>
                                    <input type="text" value={companyFormData.linkedin} onChange={e => setCompanyFormData({ ...companyFormData, linkedin: e.target.value })} className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-sm font-bold outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]" />
                                </div>
                            </div>

                            <EnrichmentKeyNotice
                                onManage={() => {
                                    // Leaving this view unmounts the dashboard and drops the draft: ask first.
                                    const dirty = Object.entries(companyFormData).some(([k, v]) => v && !(k === 'billingCurrency' && v === 'INR'))
                                        || newProjectMeta.projectName || newProjectMeta.certification;
                                    if (dirty && !window.confirm('Leaving this page will discard the company details you entered. Continue?')) return false;
                                    setIsAddCompanyOpen(false);
                                    return true;
                                }}
                            />

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Contact Person (authorized)</label>
                                <input type="text" value={companyFormData.contactName} onChange={e => setCompanyFormData({ ...companyFormData, contactName: e.target.value })} className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-sm font-bold outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]" />
                            </div>

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Authorized Phone</label>
                                    <input type="text" value={companyFormData.contactNumber} onChange={e => setCompanyFormData({ ...companyFormData, contactNumber: e.target.value })} className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-sm font-bold outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Authorized Email</label>
                                    <input type="email" value={companyFormData.contactEmail} onChange={e => setCompanyFormData({ ...companyFormData, contactEmail: e.target.value })} className="w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_80%,transparent)] px-5 py-4 text-sm font-bold outline-none transition-colors focus:border-[color:var(--color-info-line)] focus:bg-[color:var(--color-bg-white)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--color-accent)_20%,transparent)]" />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 rounded-3xl border border-[color:color-mix(in_srgb,var(--color-info-line)_80%,transparent)] bg-[color:color-mix(in_srgb,var(--color-accent-tint)_40%,transparent)] p-6 sm:p-8">
                            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[color:var(--color-accent)]">Contractual / Billing</div>
                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Official Billing Address</label>
                                <textarea value={companyFormData.billingAddress} onChange={e => setCompanyFormData({ ...companyFormData, billingAddress: e.target.value })} className="w-full px-4 py-3 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] rounded-xl text-sm font-bold outline-none resize-none" rows={2}></textarea>
                            </div>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Bill to Attention</label>
                                    <input type="text" value={companyFormData.billToPersonName} onChange={e => setCompanyFormData({ ...companyFormData, billToPersonName: e.target.value })} className="w-full px-4 py-3 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] rounded-xl text-sm font-bold outline-none" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Bill to Department</label>
                                    <input type="text" value={companyFormData.billToDepartment} onChange={e => setCompanyFormData({ ...companyFormData, billToDepartment: e.target.value })} className="w-full px-4 py-3 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] rounded-xl text-sm font-bold outline-none" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Currency of Settlement</label>
                                <select
                                    value={companyFormData.billingCurrency}
                                    onChange={e => setCompanyFormData({ ...companyFormData, billingCurrency: e.target.value })}
                                    className="w-full px-4 py-3 bg-[color:var(--color-bg-white)] border border-[color:var(--color-info-line)] rounded-xl text-sm font-black outline-none"
                                >
                                    <option value="INR">INR - Indian Rupee</option>
                                    <option value="USD">USD - US Dollar</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="GBP">GBP - British Pound</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {deleteTarget && typeof document !== 'undefined'
                ? createPortal(
                    <div className="fixed inset-0 z-[1990] flex animate-in fade-in items-center justify-center p-4 duration-200">
                        <div
                            className="absolute inset-0 bg-[color:color-mix(in_srgb,var(--color-accent-fill)_55%,transparent)] backdrop-blur-md"
                            onClick={() => !isDeleting && setDeleteTarget(null)}
                            role="presentation"
                            aria-hidden
                        />
                        <div
                            className="relative mx-4 w-full max-w-md animate-in zoom-in-95 overflow-hidden rounded-3xl bg-[color:var(--color-bg-white)] shadow-2xl duration-300"
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="delete-project-dialog-title"
                        >
                            <div className="p-8 text-center">
                                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[color:var(--color-danger-bg)]">
                                    <svg className="h-7 w-7 text-[color:var(--color-danger-fg)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </div>
                                <h3 id="delete-project-dialog-title" className="mb-2 text-xl font-black text-[color:var(--color-text-hero)]">Delete Project</h3>
                                <p className="text-sm leading-relaxed text-[color:var(--color-text-muted)]">
                                    Are you sure you want to permanently delete <span className="font-bold text-[color:var(--color-text-hero)]">"{deleteTarget.name}"</span>?
                                    This will remove all project members, notifications, and associated data. If this is the only project under the company, the company will also be deleted.
                                </p>
                                <p className="mt-3 text-xs font-bold uppercase tracking-wide text-[color:var(--color-danger-fg)]">This action cannot be undone.</p>
                            </div>
                            <div className="flex border-t border-[color:var(--color-border)]">
                                <button
                                    type="button"
                                    onClick={() => setDeleteTarget(null)}
                                    disabled={isDeleting}
                                    className="flex-1 py-4 text-sm font-bold text-[color:var(--color-text-muted)] transition-colors hover:bg-[color:var(--color-bg-alt)]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDeleteProject}
                                    disabled={isDeleting}
                                    className="flex-1 bg-[color:var(--color-danger-fg)] py-4 text-sm font-black text-white transition-colors hover:brightness-95 disabled:opacity-60"
                                >
                                    {isDeleting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                            Deleting...
                                        </span>
                                    ) : 'Delete Permanently'}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </PageShell>
    );
};

export default DashboardView;
